import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@lims/permissions';
import { samplesApi } from '../../api/endpoints.js';
import type { Sample } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import { useLaboratoryTimeZone } from '../../app/laboratory-time-zone.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  DateTimeField,
  Dialog,
  Panel,
  ReadOnlyField,
  TextAreaField,
  TextField,
  useToast,
} from '../../components/ui/index.js';
import { formatDateTime } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';

/**
 * Where the material is kept, and the record of its destruction.
 *
 * ===========================================================================
 * DISPOSAL IS ALWAYS A RECORDED HUMAN ACT. NOTHING HERE IS AUTOMATIC (D71).
 * ===========================================================================
 *
 * A physical sample is evidence and cannot be restored from a backup, so this
 * product never destroys one on a schedule. A retention period produces a DUE
 * DATE — a prompt for a person — and nothing else. The due date shown below is
 * computed by the backend from this laboratory's own configured period at the
 * moment it is read: there is no stored due-date column, so changing the policy
 * changes the answer rather than leaving stale dates behind.
 *
 * NO DUE DATE IS NOT AN OVERSIGHT. The default retention is "keep it", which is
 * what a laboratory that has configured nothing gets. Said in words on the
 * panel, not left as a blank.
 *
 * DISPOSAL DESTROYS THE MATERIAL AND KEEPS THE RECORD. The sample number, its
 * arrival, its decisions, its pieces and its custody chain all survive, and the
 * disposal itself cannot be undone or repeated — the database refuses both. A
 * method must be stated, because "disposed of" with no account of how is not a
 * record anybody can rely on.
 */
export function SampleStoragePanel({ sample }: { readonly sample: Sample }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const timeZone = useLaboratoryTimeZone();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const locale = currentLocale();

  const storage = sample.storage;

  const [isEditing, setIsEditing] = useState(false);
  const [location, setLocation] = useState('');
  const [shelfOrBin, setShelfOrBin] = useState('');
  const [note, setNote] = useState('');

  const [isDisposing, setIsDisposing] = useState(false);
  const [method, setMethod] = useState('');
  const [witness, setWitness] = useState('');
  const [disposedAt, setDisposedAt] = useState<string | null>(null);
  const [disposalNote, setDisposalNote] = useState('');

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['sample', sample.id] });
    await queryClient.invalidateQueries({ queryKey: ['samples'] });
  };

  const save = useMutation({
    mutationFn: () =>
      samplesApi.setStorage(sample.id, {
        location: location.trim() === '' ? null : location.trim(),
        shelfOrBin: shelfOrBin.trim() === '' ? null : shelfOrBin.trim(),
        note: note.trim() === '' ? null : note.trim(),
      }),
    onSuccess: async () => {
      await invalidate();
      show({ tone: 'success', message: t('samples.storageSaved') });
      setIsEditing(false);
    },
  });

  const dispose = useMutation({
    mutationFn: () =>
      samplesApi.dispose(sample.id, {
        method: method.trim(),
        ...(witness.trim() ? { witness: witness.trim() } : {}),
        ...(disposedAt
          ? { disposedAt }
          : {}),
        ...(disposalNote.trim() ? { note: disposalNote.trim() } : {}),
      }),
    onSuccess: async () => {
      await invalidate();
      show({ tone: 'success', message: t('samples.disposalRecorded') });
      setIsDisposing(false);
    },
  });

  const mayManage = can(PERMISSIONS.SAMPLE_STORAGE_MANAGE);

  return (
    <Panel
      title={t('samples.storageTitle')}
      description={t('samples.storageHint')}
      actions={
        mayManage && !sample.isDisposed && !sample.isVoided ? (
          <ActionBar>
            <ActionButton
              priority="secondary"
              size="sm"
              onPress={() => {
                setLocation(storage?.location ?? '');
                setShelfOrBin(storage?.shelfOrBin ?? '');
                setNote(storage?.note ?? '');
                setIsEditing(true);
              }}
            >
              {t('samples.setStorage')}
            </ActionButton>
            <ActionButton
              priority="destructive"
              size="sm"
              onPress={() => setIsDisposing(true)}
            >
              {t('samples.recordDisposal')}
            </ActionButton>
          </ActionBar>
        ) : null
      }
    >
      <div className="grid grid-cols-1 gap-gutter sm:grid-cols-2">
        <ReadOnlyField label={t('samples.storageLocation')}>
          {storage?.location ?? t('common.notRecorded')}
        </ReadOnlyField>
        <ReadOnlyField label={t('samples.shelfOrBin')}>
          {storage?.shelfOrBin ?? t('common.notRecorded')}
        </ReadOnlyField>
        <ReadOnlyField label={t('samples.disposalDueAt')}>
          {/*
            No due date means "kept until a person disposes of it". Said in
            words: a dash would read as a value somebody forgot to enter.
          */}
          {sample.disposalDueAt
            ? formatDateTime(sample.disposalDueAt, { locale })
            : t('samples.keptIndefinitely')}
        </ReadOnlyField>
        <ReadOnlyField label={t('samples.storageNote')}>
          {storage?.note ?? t('common.notRecorded')}
        </ReadOnlyField>
      </div>

      {sample.isDisposed ? (
        <div className="mt-gutter">
          <Alert
            tone="warning"
            title={t('samples.disposedTitle')}
            description={t('samples.disposedBody')}
          />
          <div className="mt-stack grid grid-cols-1 gap-gutter sm:grid-cols-2">
            <ReadOnlyField label={t('samples.disposedAt')}>
              {formatDateTime(storage?.disposedAt, { locale }) ??
                t('common.notRecorded')}
            </ReadOnlyField>
            <ReadOnlyField label={t('samples.disposalMethod')}>
              {storage?.disposalMethod ?? t('common.notRecorded')}
            </ReadOnlyField>
            <ReadOnlyField label={t('samples.disposalWitness')}>
              {storage?.disposalWitness ?? t('common.notRecorded')}
            </ReadOnlyField>
          </div>
        </div>
      ) : (
        <p className="mt-stack text-caption text-default-600">
          {t('samples.disposalNeverAutomaticNotice')}
        </p>
      )}

      <Dialog
        isOpen={isEditing}
        onClose={() => setIsEditing(false)}
        title={t('samples.setStorage')}
        footer={
          <ActionBar>
            <ActionButton
              priority="tertiary"
              onPress={() => setIsEditing(false)}
              isDisabled={save.isPending}
            >
              {t('common.cancel')}
            </ActionButton>
            <ActionButton
              priority="primary"
              isLoading={save.isPending}
              onPress={() => save.mutate()}
            >
              {t('common.save')}
            </ActionButton>
          </ActionBar>
        }
      >
        <div className="flex flex-col gap-stack">
          {/*
            Free text, and no list of locations ships with the product: a
            laboratory's shelves are its own, and a seeded "Shelf A" would be
            one laboratory's habit written into everybody's code
            (CLAUDE.md section 6.1).
          */}
          <TextField
            label={t('samples.storageLocation')}
            description={t('samples.storageLocationFreeTextHint')}
            value={location}
            onChange={setLocation}
          />
          <TextField
            label={t('samples.shelfOrBin')}
            value={shelfOrBin}
            onChange={setShelfOrBin}
          />
          <TextAreaField
            label={t('samples.storageNote')}
            value={note}
            onChange={setNote}
            rows={2}
          />
          {save.error ? (
            <Alert tone="danger" title={t('samples.storageFailed')} />
          ) : null}
          <p className="text-caption text-default-600">
            {t('samples.storageMovementNotice')}
          </p>
        </div>
      </Dialog>

      <Dialog
        isOpen={isDisposing}
        onClose={() => setIsDisposing(false)}
        title={t('samples.recordDisposal')}
        footer={
          <ActionBar>
            <ActionButton
              priority="tertiary"
              onPress={() => setIsDisposing(false)}
              isDisabled={dispose.isPending}
            >
              {t('common.cancel')}
            </ActionButton>
            <ActionButton
              priority="destructive"
              // A method is required, and the button says so by staying
              // unavailable until one is stated. The backend refuses it too.
              isDisabled={method.trim().length < 3}
              isLoading={dispose.isPending}
              onPress={() => dispose.mutate()}
            >
              {t('samples.recordDisposal')}
            </ActionButton>
          </ActionBar>
        }
      >
        <div className="flex flex-col gap-stack">
          <Alert
            tone="warning"
            title={t('samples.disposalIrreversibleTitle')}
            description={t('samples.disposalIrreversibleBody')}
          />
          <TextField
            label={t('samples.disposalMethod')}
            description={t('samples.disposalMethodHint')}
            value={method}
            onChange={setMethod}
            isRequired
          />
          <TextField
            label={t('samples.disposalWitness')}
            value={witness}
            onChange={setWitness}
          />
          <DateTimeField
            label={t('samples.disposedAt')}
            description={t('samples.disposedAtHint')}
            value={disposedAt}
            onChange={setDisposedAt}
            timeZone={timeZone}
          />
          <TextAreaField
            label={t('samples.notes')}
            value={disposalNote}
            onChange={setDisposalNote}
            rows={2}
          />
          {dispose.error ? (
            <Alert tone="danger" title={t('samples.disposalFailed')} />
          ) : null}
        </div>
      </Dialog>
    </Panel>
  );
}
